/**
 * 棋盘渲染：CSS Grid + 每格一个 DOM 元素。
 *
 * 为什么用 DOM 而不是 Canvas：格子最多 25×25=625 个，DOM 完全撑得住；
 * 换来的是 CSS 过渡/关键帧动画直接可用（星星弹出、吉祥物帧动画都是 CSS），
 * 以及无障碍与命中测试免费。Canvas 反而要自己写帧调度与坐标换算。
 *
 * 区域划分靠颜色区分，不画粗边框 —— 这是设计稿的视觉语言：
 * 圆角色块 + 间隙，每个区域一种颜色。dim ≤ 12 时 12 色够用且互不相邻重复。
 */

import { stateKey } from '../core/cellState.js';
import { regionColor } from './palette.js';

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

export class BoardView {
    /**
     * @param {HTMLElement} root 棋盘容器
     */
    constructor(root) {
        this.root = root;
        /** @type {import('../core/session.js').GameSession | null} */
        this.session = null;
        /** @type {HTMLElement[][]} */
        this.cells = [];
        /** @type {Array<() => void>} */
        this._unsubs = [];
    }

    /**
     * 绑定会话并建出棋盘。重复调用会先清掉上一局。
     * @param {import('../core/session.js').GameSession} session
     */
    mount(session) {
        this.unmount();
        this.session = session;

        const { dim } = session;
        this.root.style.setProperty('--sd-dim', String(dim));
        this.root.replaceChildren();
        this.cells = [];
        this._gap = null; // 换盘后重新量格间距

        const frag = document.createDocumentFragment();
        for (let r = 0; r < dim; r++) {
            const row = [];
            for (let c = 0; c < dim; c++) {
                const cell = document.createElement('div');
                // sd-cellface 是外观（与引导弹窗的演示格共用），
                // sd-cell 专指「棋盘上的格子」，别拿它做外观选择器
                cell.className = 'sd-cell sd-cellface';
                cell.dataset.r = String(r);
                cell.dataset.c = String(c);
                cell.dataset.state = stateKey(session.board.get(r, c));
                cell.style.setProperty('--sd-tint', regionColor(session.board.regionAt(r, c)));

                const mark = document.createElement('span');
                mark.className = 'sd-mark';
                cell.appendChild(mark);

                frag.appendChild(cell);
                row.push(cell);
            }
            this.cells.push(row);
        }
        this.root.appendChild(frag);

        this._unsubs.push(
            session.on('cellChanged', ({ r, c }) => this.refreshCell(r, c)),
            session.on('reset', () => this.refreshAll()),
        );
    }

    unmount() {
        for (const off of this._unsubs) off();
        this._unsubs = [];
        this.session = null;
        this.cells = [];
        this.root.replaceChildren();
    }

    /** @param {number} r @param {number} c */
    cellAt(r, c) {
        return this.cells[r]?.[c] ?? null;
    }

    /**
     * 同步一格的视觉状态。
     * 状态变化通过 data-state 表达，具体长相全交给 CSS —— 换皮不用改 JS。
     * @param {number} r @param {number} c
     */
    refreshCell(r, c) {
        const cell = this.cellAt(r, c);
        if (!cell || !this.session) return;

        const key = stateKey(this.session.board.get(r, c));
        if (cell.dataset.state === key) return;
        cell.dataset.state = key;

        // 重启入场动画：先摘掉再强制回流，否则连续同类状态不会重播
        const mark = cell.firstElementChild;
        if (mark) {
            mark.classList.remove('sd-mark--enter');
            void mark.offsetWidth;
            mark.classList.add('sd-mark--enter');
        }
    }

    /** 全盘重绘（重置本关、加载新关）。 */
    refreshAll() {
        if (!this.session) return;
        for (let r = 0; r < this.session.dim; r++)
            for (let c = 0; c < this.session.dim; c++) {
                const cell = this.cellAt(r, c);
                if (cell) cell.dataset.state = stateKey(this.session.board.get(r, c));
            }
    }

    /**
     * 提示高亮：给一组格子加描边呼吸灯（提示功能用）。
     * @param {Array<{r: number, c: number}>} cells
     */
    highlight(cells) {
        this.clearHighlight();
        for (const { r, c } of cells) {
            const cell = this.cellAt(r, c);
            if (cell) cell.classList.add('sd-cell--hint');
        }
    }

    /** 清除提示高亮。 */
    clearHighlight() {
        for (const el of this.root.querySelectorAll('.sd-cell--hint')) {
            el.classList.remove('sd-cell--hint');
        }
    }

    /**
     * 鳍格标记：带鳍类提示里"漏出的那一格"。红色虚线圈，与金色高亮区分开——
     * 文案里的"红圈那格"指的就是它。
     * @param {Array<{r: number, c: number}>} cells
     */
    showFins(cells = []) {
        this.clearFins();
        for (const { r, c } of cells) {
            const cell = this.cellAt(r, c);
            if (cell) cell.classList.add('sd-cell--fin');
        }
    }

    /** 清除鳍格标记。 */
    clearFins() {
        for (const el of this.root.querySelectorAll('.sd-cell--fin')) {
            el.classList.remove('sd-cell--fin');
        }
    }

    /**
     * 提示预览：在目标格上显示"将要进行的操作"的幽灵标记
     * （半透明叉/星呼吸闪烁；清除错叉则让现有标记闪烁）。
     * @param {Array<import('../core/hintEngine.js').HintAction>} actions
     */
    preview(actions) {
        this.clearPreview();
        for (const { r, c, to } of actions) {
            const cell = this.cellAt(r, c);
            if (!cell) continue;
            // to: 1=STAR 2=WHITE_CROSS 0=EMPTY(清错叉) —— 与 cellState 对齐
            cell.dataset.preview = to === 1 ? 'star' : to === 2 ? 'whiteCross' : 'erase';
        }
    }

    /** 清除提示预览。 */
    clearPreview() {
        for (const el of this.root.querySelectorAll('.sd-cell[data-preview]')) {
            delete el.dataset.preview;
        }
    }

    /**
     * 行列号徽标：提示文案点名「第 X 行 / 第 Y 列」时，在棋盘左侧/顶部
     * 浮出对应的数字徽标 —— 文字和棋盘互相指认，玩家不用自己数格子。
     * 位置按格子的 offset 现算：提示展示期间棋盘不滚动不换盘，够用；
     * 转屏/改窗口尺寸时提示本来就该重取（盘面视觉都变了），不做跟随。
     * @param {{rows: number[], cols: number[]}} refs 1-based 行/列号
     */
    showLineBadges({ rows = [], cols = [] } = {}) {
        this.clearLineBadges();
        if (!this.session) return;
        const make = (n, isRow) => {
            const cell = isRow ? this.cellAt(n - 1, 0) : this.cellAt(0, n - 1);
            if (!cell) return;
            const b = document.createElement('span');
            b.className = `sd-line-badge sd-line-badge--${isRow ? 'row' : 'col'}`;
            // 行用数字、列用字母（A、B、C…）——两轴各有一套记号，不会互相混淆
            b.textContent = isRow ? String(n) : String.fromCharCode(64 + n);
            if (isRow) b.style.top = `${cell.offsetTop + cell.offsetHeight / 2}px`;
            else b.style.left = `${cell.offsetLeft + cell.offsetWidth / 2}px`;
            this.root.appendChild(b);
        };
        for (const n of rows) make(n, true);
        for (const n of cols) make(n, false);
    }

    /** 清除行列号徽标。 */
    clearLineBadges() {
        for (const el of this.root.querySelectorAll('.sd-line-badge')) el.remove();
    }

    /**
     * 屏幕坐标 → 格坐标。滑动涂画时逐帧调用，用 elementFromPoint
     * 而不是缓存 rect —— 页面滚动或旋转屏时缓存会失效。
     * @param {number} clientX @param {number} clientY
     * @returns {{ r: number, c: number } | null}
     */
    /**
     * 坐标 → 格子。按棋盘几何就近归格，而不是问浏览器「这个点上是什么元素」。
     *
     * 用 elementFromPoint 的话，格与格之间的间隙和圆角切掉的四角都不属于任何格子，
     * 点上去会被静默丢弃 —— 10×10 盘上这类死区占棋盘面积约两成，且毫无反馈。
     * 改成几何换算后整块棋盘都是活的：间隙归就近的那一格，等效可点区域从
     * 格子本身扩大到一整个格距。（同理也让滑动涂画跨过间隙时不再断。）
     *
     * @param {number} clientX @param {number} clientY
     * @returns {{r: number, c: number} | null} 落在棋盘外返回 null
     */
    cellFromPoint(clientX, clientY) {
        const dim = this.session?.dim;
        if (!dim) return null;

        const b = this.root.getBoundingClientRect();
        if (b.width <= 0 || b.height <= 0) return null;
        if (clientX < b.left || clientX > b.right || clientY < b.top || clientY > b.bottom) {
            return null;
        }

        // 格距 = 格子 + 间隙。由 W = dim*格子 + (dim-1)*间隙 解得 格距 = (W + 间隙) / dim
        const gap = this._gridGap();
        const pitchX = (b.width + gap) / dim;
        const pitchY = (b.height + gap) / dim;

        // 加半个间隙再取整：间隙从中线劈开、各归就近的一格，
        // 每格的命中区才与它的视觉框同心。直接取整会把整条间隙都算给左/上那格，
        // 命中区相对看到的格子整体偏移半个间隙，瞄准边沿时会打到隔壁。
        const c = clamp(Math.floor((clientX - b.left + gap / 2) / pitchX), 0, dim - 1);
        const r = clamp(Math.floor((clientY - b.top + gap / 2) / pitchY), 0, dim - 1);
        return { r, c };
    }

    /**
     * 量一次格间距并缓存（换盘时作废）。
     * 注意：`?tune=1` 面板实时拖动间隙不会刷新这个缓存，要重开一关才生效 —— 仅影响开发面板。
     */
    _gridGap() {
        if (this._gap === null || this._gap === undefined) {
            this._gap = parseFloat(getComputedStyle(this.root).columnGap) || 0;
        }
        return this._gap;
    }

    /**
     * 棋盘逐格入场动画：从左下到右上按对角线波次出现。
     * 延迟写在 CSS 变量里，动画本身由 CSS 定义。
     * @param {number} [stepMs] 相邻波次间隔
     * @returns {Promise<void>} 全部播完
     */
    playEnter(stepMs = 26) {
        if (!this.session) return Promise.resolve();
        const dim = this.session.dim;
        const waves = 2 * dim - 1;

        for (let r = 0; r < dim; r++)
            for (let c = 0; c < dim; c++) {
                const cell = this.cellAt(r, c);
                if (!cell) continue;
                const wave = (dim - 1 - r) + c; // 左下角最先
                cell.style.setProperty('--sd-enter-delay', `${wave * stepMs}ms`);
                cell.classList.remove('sd-cell--enter');
                void cell.offsetWidth;
                cell.classList.add('sd-cell--enter');
            }

        return new Promise(resolve => {
            setTimeout(resolve, waves * stepMs + 260);
        });
    }
}
